import { Disclosure, Transition } from '@headlessui/react';
import dayjs, { Dayjs } from 'dayjs';
import { cloneDeep } from 'lodash';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import omit from 'lodash/omit';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiltersGroup } from '@/modules/common/components/FiltersGroup.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { PaymentTabFilters } from '@/modules/issuer-dashboard/components/operation-tabs/IssuerPaymentsTab.tsx';
import { CalendarInput, RangeNumberInput, Select, SelectOption } from 'ui';
import { getDisabledDate } from 'ui/utils';
import { PaymentDirection, PaymentStatus } from 'api';

interface IssuerPaymentsFilters {
  filters: PaymentTabFilters;
  setFilters: (cb: (filters: PaymentTabFilters) => PaymentTabFilters) => void;
}

const statusOptions: SelectOption<PaymentStatus>[] = Object.values(PaymentStatus).map(value => ({
  label: capitalize(value),
  value,
}));

const directionOptions: SelectOption<PaymentDirection>[] = Object.values(PaymentDirection).map(
  value => ({
    label: capitalize(lowerCase(value)),
    value,
  }),
);

const IssuerPaymentsFilters = ({ filters, setFilters }: IssuerPaymentsFilters) => {
  const [params] = useSearchParams();

  const {
    status,
    direction,
    entity_id,
    amount_from,
    amount_to,
    requested_at_date_from,
    requested_at_date_to,
    confirmation_date_from,
    confirmation_date_to,
  } = filters;

  const registerId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');

  const handleChangeStatus = useCallback(
    (status: PaymentStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const handleChangeDirection = useCallback(
    (direction: PaymentDirection | null) => {
      setFilters(filters => (direction ? { ...filters, direction } : omit(filters, 'direction')));
    },
    [setFilters],
  );

  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id: registerId }, { disabled: !registerId });

  const registerShareholderListOptions = useMemo<SelectOption<number>[]>(
    () =>
      registerShareholderList?.shareholders?.map(({ entity_id, entity: { entity_name } }) => ({
        label: entity_name,
        value: entity_id,
      })) || [],
    [registerShareholderList],
  );

  const handleChangeEntity = useCallback(
    (entity_id: number | null) => {
      setFilters(filters => (entity_id ? { ...filters, entity_id } : omit(filters, 'entity_id')));
    },
    [setFilters],
  );

  const handleChangeAmount = useCallback(
    (value: Array<string | undefined>) => {
      setFilters(filters => {
        let res = cloneDeep(filters);

        if (value[0]) res.amount_from = +value[0];
        else res = omit(res, ['amount_from']);

        if (value[1]) res.amount_to = +value[1];
        else res = omit(res, ['amount_to']);

        return res;
      });
    },
    [setFilters],
  );

  const handleChangeRequestedDate = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setFilters(filters =>
        dates && dates[0] && dates[1]
          ? {
              ...filters,
              requested_at_date_from: dates[0].toISOString(),
              requested_at_date_to: dates[1].endOf('d').toISOString(),
            }
          : omit(filters, ['requested_at_date_from', 'requested_at_date_to']),
      );
    },
    [setFilters],
  );

  const handleChangeConfirmationDate = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setFilters(filters =>
        dates && dates[0] && dates[1]
          ? {
              ...filters,
              confirmation_date_from: dates[0].toISOString(),
              confirmation_date_to: dates[1].endOf('d').toISOString(),
            }
          : omit(filters, ['confirmation_date_from', 'confirmation_date_to']),
      );
    },
    [setFilters],
  );

  return (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
    >
      <Disclosure.Panel static>
        <div className="w-full flex flex-col items-start pb-2.5">
          <FiltersGroup className="w-full flex items-end gap-4 flex-wrap [&>*]:min-w-[230px] [&>*]:w-fit [&>*]:max-w-[300px] [&>*]:flex-1">
            <Select
              isClearable
              label="status"
              placeholder="Any status"
              options={statusOptions}
              value={status}
              onChange={handleChangeStatus}
            />
            <Select
              isClearable
              label="direction"
              placeholder="Any direction"
              options={directionOptions}
              value={direction}
              onChange={handleChangeDirection}
            />
            <Select
              isClearable
              label="Holder"
              placeholder="Any holder"
              isLoading={isRegisterShareholderListLoading}
              options={registerShareholderListOptions}
              value={entity_id}
              onChange={handleChangeEntity}
            />
            <RangeNumberInput
              clearable
              label="Amount $"
              value={[amount_from?.toString(), amount_to?.toString()]}
              onChange={handleChangeAmount}
            />
            <CalendarInput
              range
              disabledDate={getDisabledDate}
              label="request date"
              className="[&>div]:!py-3 [&>label]:!m-0"
              name="requested_at_date"
              placeholder="Any date"
              rangeValue={
                requested_at_date_from && requested_at_date_to
                  ? [dayjs(requested_at_date_from), dayjs(requested_at_date_to)]
                  : null
              }
              rangeOnChange={handleChangeRequestedDate}
            />
            <CalendarInput
              range
              disabledDate={getDisabledDate}
              label="confirmation date"
              className="[&>div]:!py-3 [&>label]:!m-0"
              name="confirmation_date"
              placeholder="Any date"
              rangeValue={
                confirmation_date_from && confirmation_date_to
                  ? [dayjs(confirmation_date_from), dayjs(confirmation_date_to)]
                  : null
              }
              rangeOnChange={handleChangeConfirmationDate}
            />
          </FiltersGroup>
        </div>
      </Disclosure.Panel>
    </Transition>
  );
};

export default IssuerPaymentsFilters;
